import * as React from 'react';
import cx from 'classnames';
import { uniqBy } from 'lodash';
import moment from 'moment-timezone';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Coin, { CoinColor } from 'components/Coin';
import Heading from 'components/Heading';
import Link from 'components/Link';
import List from 'components/List';
import ListItem from 'components/ListItem';
import Row from 'components/Row';
import Text, { TextColor } from 'components/Text';

import './styles/DateTimeAvailabilitySelect.scss';

export type Props = {
  // the selected datetime option
  value?: moment.Moment;
  // callback for when a user selects a datetime option
  onChange?: (selectedDate: moment.Moment) => void;
  // the specific datetime options that are available for the user
  // to select
  dateTimeOptions: moment.Moment[];
  // the range of days to show at the top of the select
  daysToShow: moment.Moment[];
  // callback for when a user selects a day
  onSelectDay?: () => void;
  // callback for when a user expands availabilities
  onMoreAvailabilities?: () => void;
  // callback for when a user collapses availabilities
  onLessAvailabilities?: () => void;
  // callback for when a day is empty
  onEmptyDay?: (day: string) => void;
  // empty state text when no availabilities exist
  emptyText?: string;
};

type State = {
  currentlyViewedDay?: moment.Moment;
  showingAdditionalAvailabilities: boolean;
};

class DateTimeAvailabilitySelect extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentlyViewedDay: props.daysToShow[0],
      showingAdditionalAvailabilities: false,
    };
  }

  componentDidUpdate() {
    const newDays = this.props.daysToShow;
    if (
      this.state.currentlyViewedDay &&
      !newDays.some(day => day.isSame(this.state.currentlyViewedDay, 'day'))
    ) {
      // if the daysToShow change, we should adjust the selected display day
      this.handleSelectDisplayDay(newDays[0]);
    }
  }

  handleSelectDateOption = (option: moment.Moment) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(option);
    }
  };

  handleSelectDisplayDay = (day: moment.Moment) => {
    if (this.props.onSelectDay) {
      this.props.onSelectDay();
    }
    this.setState({ currentlyViewedDay: day });
  };

  datesAreSame = (day1: moment.Moment, day2: moment.Moment) => {
    return day1.date() === day2.date() && day1.month() === day2.month();
  };

  getDateTimeOptionsForDisplayDay = (day: moment.Moment) => {
    return this.props.dateTimeOptions.filter(option => this.datesAreSame(option, day));
  };

  renderDisplayDay = (
    dayOfWeekLabel: string,
    dateOfMonthLabel: string,
    dayIsSelected: boolean,
    nextDayIsNextWeek: boolean,
    dayHasNoOptions: boolean,
    onClick: () => void
  ) => {
    const borderAndTextColor = (function(): [CoinColor, TextColor] {
      if (dayIsSelected && dayHasNoOptions) {
        return ['pitch', 'thunder'];
      } else if (dayIsSelected) {
        return ['pitch', 'pitch'];
      } else {
        return ['white', 'thunder'];
      }
    })();

    return (
      <ListItem
        key={`${dayOfWeekLabel}-${dateOfMonthLabel}`}
        className={cx(
          'DateTimeAvailabilitySelect-dayItem',
          nextDayIsNextWeek && 'DateTimeAvailabilitySelect-dayItem-endOfWeek'
        )}>
        <Link onClick={onClick} data-test="display-day-list-item">
          <Text align="center">
            <Row size="xxsmall">
              <Text size="xxxsmall" color="thunder">
                {dayOfWeekLabel}
              </Text>
            </Row>
            <Coin
              className={cx(
                'DateTimeAvailabilitySelect-dayItem-coin',
                dayHasNoOptions && 'DateTimeAvailabilitySelect-dayItem-coin--strikethrough'
              )}
              color="white"
              border={borderAndTextColor[0]}
              size="xxxlarge"
              thick>
              <Text size="medium" color={borderAndTextColor[1]}>
                {dateOfMonthLabel}
              </Text>
            </Coin>
          </Text>
        </Link>
      </ListItem>
    );
  };

  renderDaysToShow = () => {
    const uniqueDaysToShow = uniqBy(this.props.daysToShow, day => day.date());
    const { currentlyViewedDay } = this.state;
    if (!currentlyViewedDay) {
      return null;
    }

    return (
      <List horizontal size="medium">
        {uniqueDaysToShow.map((day, index, days) => {
          const nextDay = days[index + 1];
          const nextDayIsNextWeek = nextDay && nextDay.isAfter(day, 'week');
          return this.renderDisplayDay(
            day.format('ddd'),
            day.format('D'),
            this.datesAreSame(day, currentlyViewedDay),
            nextDayIsNextWeek,
            this.getDateTimeOptionsForDisplayDay(day).length === 0,
            () => this.handleSelectDisplayDay(day)
          );
        })}
      </List>
    );
  };

  renderDateTimeOptions = () => {
    const { currentlyViewedDay } = this.state;
    if (!currentlyViewedDay) {
      return null;
    }

    const options = this.getDateTimeOptionsForDisplayDay(currentlyViewedDay);
    if (options.length === 0) {
      if (this.props.onEmptyDay) {
        this.props.onEmptyDay(currentlyViewedDay.format('ddd, MMM D'));
      }
      return <Text color="thunder">No availability. Try changing the date.</Text>;
    }

    const { onLessAvailabilities, onMoreAvailabilities } = this.props;
    const { showingAdditionalAvailabilities } = this.state;
    const linkText = showingAdditionalAvailabilities ? 'Show less' : 'Show more availability';
    const availabilityCap = showingAdditionalAvailabilities ? undefined : 4;

    return (
      <React.Fragment>
        <div>
          {options.slice(0, availabilityCap).map(option => {
            const optionIsSelected = option.isSame(this.props.value, 'second');
            return (
              <Link
                key={option.toISOString()}
                onClick={() => this.handleSelectDateOption(option)}
                data-test="date-time-option">
                <Text
                  className={cx(
                    'DateTimeAvailabilitySelect-dateTimeOption',
                    optionIsSelected && 'DateTimeAvailabilitySelect-dateTimeOption--selected'
                  )}
                  color={optionIsSelected ? 'pitch' : 'thunder'}
                  size="small">
                  {option.format('LT')}
                </Text>
              </Link>
            );
          })}
        </div>
        {options.length > 4 && (
          <Link
            data-test="toggle-availability-count-link"
            underline
            color="pitch"
            onClick={() => {
              if (showingAdditionalAvailabilities && onLessAvailabilities) {
                // if we are already showing additional availabilities, trigger
                // callback for collapsing
                onLessAvailabilities();
              } else if (!showingAdditionalAvailabilities && onMoreAvailabilities) {
                // if we are not showing additional availabilities, trigger
                // callback for expanding
                onMoreAvailabilities();
              }
              this.setState({ showingAdditionalAvailabilities: !showingAdditionalAvailabilities });
            }}>
            {linkText}
          </Link>
        )}
      </React.Fragment>
    );
  };

  render() {
    const renderComponent = this.state.currentlyViewedDay ? (
      <>
        <Row size="small">{this.renderDaysToShow()}</Row>
        <Text align="left" className="DateTimeAvailabilitySelect-dateTimeOptionsArea">
          <Row size="xxsmall">
            <Heading level="4">{this.state.currentlyViewedDay.format('ddd, MMM D')}</Heading>
          </Row>
          {this.renderDateTimeOptions()}
        </Text>
      </>
    ) : (
      <Heading level="4" flush>
        {this.props.emptyText ??
          'We could not find any available dates for your selected options. Please try again.'}
      </Heading>
    );

    return (
      <Box noShadow className="DateTimeAvailabilitySelect" inline>
        <BoxBody size="small">{renderComponent}</BoxBody>
      </Box>
    );
  }
}

export default DateTimeAvailabilitySelect;
